import { Field, Form, Formik, FormikProps } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Container, FormControl, Row, Spinner } from 'react-bootstrap';
import { FiMapPin } from 'react-icons/fi';
import { IoIosArrowBack } from 'react-icons/io';
import ReactInputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { useViaCepSearch } from '@/resources/hooks/useAddressUtils';
import { useCartContext } from '@/resources/hooks/useCartContext';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { create, getAddresses, IAddressResponse } from '@/routes/addresses';
import { maskZipcode } from '@/services/global';
import { createPlacesApiClient } from '@/services/places/factories/create_places_api_client';
import { PlacesApiClient } from '@/services/places/places_api';
import { mapPlaceToAddressData } from '@/services/places/places_api/mappers/place_to_address_data';
import type { PlaceSuggestion } from '@/services/places/places_api/types';

import { setShipmentAddress } from '../../../../../routes/order';
import ChooseCard from '../../../cart/shipment/chooseCard/ChooseCard';
import Error from '../../Form/Error';
import { AddAddressForm } from './AddAddressForm';
import { IAddressModalStep, IAddressValues } from './types';

interface Props {
  orderId: number;
  clientId: number;
  setModalStep: (step: IAddressModalStep) => void;
  setClientAddresses: (addresses: IAddressResponse[]) => void;
}

function Footer({ onManualClick }: { onManualClick?: () => void }) {
  return (
    <Button variant="link" type="button" onClick={onManualClick}>
      Adicionar Manualmente
    </Button>
  );
}

function SearchAddress({ orderId, clientId, setModalStep, setClientAddresses }: Props) {
  const { configs } = useWebContext();

  const { setMinOrderAmount, setWebappOutOfDelivery } = useCartContext();
  const [error, setError] = useState<string>('');
  const [addressInput, setAddressInput] = useState<string>('');
  const [submitButton, setSubmitButton] = useState<boolean>(false);
  const [addressSelected, setAddressSelected] = useState<boolean>(false);
  const [showManualForm, setShowManualForm] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const placesApiClient = useRef<PlacesApiClient | null>(null);

  const [shouldShowMissingInputs, setShouldShowMissingInputs] = useState<Record<string, boolean>>({
    street: false,
    neighborhood: false,
  });

  useEffect(() => {
    try {
      placesApiClient.current = createPlacesApiClient();
    } catch (error) {
      console.error('Failed to initialize Places API:', error);
    }
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error);
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error]);

  const schema = yup.object().shape({
    zipcode: yup
      .string()
      .required('CEP é obrigatório.')
      .transform((zipcode) => zipcode.replace('-', ''))
      .matches(/^\d{8}$/, 'Insira um cep válido.'),
    number: yup.string().required('Número obrigatório.'),
    city: yup.string(),
    state: yup.string(),
    street: yup.string().required('Rua obrigatória.'),
    country: yup.string(),
    neighborhood: yup.string().required('Bairro obrigatório.'),
    complement: yup.string(),
    reference: yup.string(),
    client_id: yup.number().required(),
  });

  const handleInput = (value: string) => {
    setAddressInput(value);
    setShowSuggestions(value.length > 0);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (value.length > 2 && placesApiClient.current) {
      debounceTimer.current = setTimeout(async () => {
        setIsLoading(true);
        try {
          const searchResults = await placesApiClient.current!.suggestPlaces({ input: value });
          setSuggestions(searchResults);
        } catch (error) {
          console.error('Error searching places:', error);
          setSuggestions([]);
        } finally {
          setIsLoading(false);
        }
      }, 300);
    } else {
      setSuggestions([]);
      setIsLoading(false);
    }
  };

  const handleSelect = async (
    suggestion: PlaceSuggestion,
    formikProps: FormikProps<IAddressValues>,
  ) => {
    const { setFieldValue } = formikProps;

    setAddressInput(suggestion.description);
    setShowSuggestions(false);
    setSuggestions([]);
    setAddressSelected(true);
    setIsLoading(true);

    try {
      if (placesApiClient.current) {
        const placeDetails = await placesApiClient.current.getPlaceDetails(suggestion.placeId);
        const addressData = mapPlaceToAddressData(placeDetails);

        setFieldValue('street', addressData.street || '');
        setFieldValue('country', addressData.country || '');
        setFieldValue('zipcode', addressData.zipcode || '');
        setFieldValue('number', addressData.number || '');
        setFieldValue('neighborhood', addressData.neighborhood || '');
        setFieldValue('city', addressData.city || '');
        setFieldValue('state', addressData.state || '');
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      setError('Erro ao obter detalhes do endereço');
    } finally {
      setIsLoading(false);
    }
  };

  const renderSuggestions = (
    suggestions: PlaceSuggestion[],
    formikProps: FormikProps<IAddressValues>,
  ) => {
    return (
      <>
        {suggestions.map((suggestion, index) => {
          const isTheLastCard = index + 1 === suggestions.length;

          return (
            <ChooseCard
              key={suggestion.placeId}
              handleClick={() => handleSelect(suggestion, formikProps)}
              styles={{ border: 'none' }}
              classes={`rounded-0 my-0 ${!isTheLastCard && 'border-bottom'}`}
            >
              <>
                <div className="d-flex align-items-center">
                  <div>
                    <FiMapPin color="#333" size="13pt" />
                  </div>
                  <div className="ml-2 w-100" style={{ lineHeight: 1 }}>
                    <h6 className="mb-0">{suggestion.mainText} </h6>
                    <p className="mb-0 text-secondary" style={{ fontSize: '11pt' }}>
                      {suggestion.secondaryText}
                    </p>
                  </div>
                </div>
              </>
            </ChooseCard>
          );
        })}
      </>
    );
  };

  const submit = (address: IAddressValues) => {
    setSubmitButton(true);
    address.zipcode = address.zipcode.replace('-', '');

    if (!address.zipcode) {
      setError('Preencha o CEP corretamente');
      return;
    }

    create(address)
      .then(({ id }) => {
        setShipmentAddress(orderId, id, { type: 'delivery' })
          .then((order) => {
            setMinOrderAmount(order?.shipment.min_order_amount ?? 0);
            setWebappOutOfDelivery(!!order.shipment.webapp_out_of_delivery);
            setError('');
            setSubmitButton(false);
            setModalStep('scheduling');
          })
          .catch(console.error);
      })
      .then(() => {
        getAddresses(clientId).then((addresses) => {
          setClientAddresses(addresses);
          setModalStep('address');
        });
      })
      .catch((e) => {
        setSubmitButton(false);
        if (e.response?.data?.min_order_amount) {
          setMinOrderAmount(e.response.data.min_order_amount || 0);
          return;
        }
        setMinOrderAmount(0);
        setWebappOutOfDelivery(!!e.response?.data?.webapp_out_of_delivery);

        setError(e.response?.data?.message ?? 'Houve um erro ao adicionar esse endereço');
      });
  };

  const renderBody = (props: FormikProps<IAddressValues>): JSX.Element => {
    const { setFieldValue } = props;
    useEffect(() => {
      setShouldShowMissingInputs({
        street: !props.values.street,
        neighborhood: !props.values.neighborhood,
      });
    }, [props.values.zipcode]);

    const handlePasteZipcode = async (e: React.ClipboardEvent<HTMLInputElement>) => {
      const value = e.clipboardData?.getData('text/plain');
      const input = e.target as HTMLInputElement;
      input.value = maskZipcode(value);
      const data = await useViaCepSearch(value);
      if (!data) return;
      setFieldValue('street', data.street || '');
      setFieldValue('city', data.city || '');
      setFieldValue('state', data.state || '');
      setFieldValue('neighborhood', data.neighborhood || '');
      setFieldValue('zipcode', data.zipcode || '');
    };

    const handleChangeZipcode = async (e: React.ChangeEvent<HTMLInputElement>) => {
      e.target.value = maskZipcode(e.target.value);
      setFieldValue('zipcode', maskZipcode(e.target.value));
      if (e.target.value.length === 9) {
        const data = await useViaCepSearch(e.target.value);

        if (!data) return;
        setFieldValue('street', data.street || '');
        setFieldValue('city', data.city || '');
        setFieldValue('state', data.state || '');
        setFieldValue('neighborhood', data.neighborhood || '');
        setFieldValue('zipcode', data.zipcode || '');
      }
    };

    if (showManualForm) return <AddAddressForm setModalStep={setModalStep} />;
    if (addressSelected) {
      return (
        <Container className="my-2">
          <Row>
            <Col>
              <p className="mb-4 text-center">Confirme seu endereço</p>
            </Col>
          </Row>
          <Row>
            <Col xs={12} className="text-center">
              <h6 className="mb-1">{props.values.street}</h6>
              <p className="mb-0 text-secondary" style={{ fontSize: '11pt' }}>
                {props.values.neighborhood && `${props.values.neighborhood}, `}
                {props.values.city && `${props.values.city} - `}
                {props.values.state && `${props.values.state}, `}
                {props.values.country}
              </p>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs={5}>
              <small className="font-weight-bold">Número</small>
              <Field
                type="text"
                name="number"
                autoComplete="off"
                placeholder="Número"
                className="form-control form-control-sm"
              />
              <Error name="number" />
            </Col>
            <Col xs={7}>
              <small className="font-weight-bold">CEP</small>
              <Field name="zipcode">
                {({ field }: { field: any }) => (
                  <FormControl
                    {...field}
                    as={ReactInputMask}
                    mask="99999-999"
                    placeholder="_____-___"
                    onPaste={handlePasteZipcode}
                    onChange={handleChangeZipcode}
                    className="form-control form-control-sm"
                  />
                )}
              </Field>
              <Error name="zipcode" />
            </Col>
          </Row>
          <Row className="mt-2">
            <Col className="gap-2">
              {shouldShowMissingInputs.street && (
                <div>
                  <small className="font-weight-bold">Rua</small>
                  <Field
                    type="text"
                    name="street"
                    autoComplete="off"
                    placeholder="Rua"
                    className="form-control form-control-sm"
                  />
                  <Error name="street" />
                </div>
              )}
              {shouldShowMissingInputs.neighborhood && (
                <div className="mt-2">
                  <small className="font-weight-bold">Bairro</small>
                  <Field
                    type="text"
                    name="neighborhood"
                    autoComplete="off"
                    placeholder="Bairro"
                    className="form-control form-control-sm"
                  />
                  <Error name="neighborhood" />
                </div>
              )}

              <small className="font-weight-bold">Complemento</small>
              <Field
                type="text"
                name="complement"
                autoComplete="off"
                placeholder="Complemento"
                className="form-control form-control-sm"
              />

              <small
                className="text-primary"
                style={{ textDecoration: 'underline' }}
                onClick={() => {
                  setAddressSelected(false);
                  Object.keys(props.values).forEach((key) => props.setFieldValue(key, ''));
                }}
              >
                Buscar outro endereço
              </small>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col xs={12}>{error && <small className="text-danger">{error}</small>}</Col>
            <Col xs={12} className="text-center">
              <Button
                type="submit"
                variant="success"
                size="sm"
                className="btn-block"
                disabled={submitButton}
              >
                {submitButton ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <span>Confirmar endereço</span>
                )}
              </Button>
            </Col>
          </Row>
        </Container>
      );
    }
    return (
      <div className="position-relative">
        <button
          className="btn btn-sm position-absolute"
          onClick={() => setModalStep('address')}
          style={{
            borderTopLeftRadius: '.2rem',
            borderBottomLeftRadius: '.2rem',
            zIndex: 1,
          }}
        >
          <IoIosArrowBack />
        </button>
        <FormControl
          type="text"
          autoComplete="off"
          name="search-address"
          value={addressInput}
          onChange={(e) => handleInput(e.currentTarget.value)}
          placeholder="Busque seu endereço"
          className="form-control form-control-sm"
          style={{ paddingLeft: '2rem' }}
        />
        {showSuggestions && suggestions.length > 0 && (
          <ul className="list-unstyled mb-0">{renderSuggestions(suggestions, props)}</ul>
        )}

        {isLoading && (
          <div className="text-center p-2">
            <Spinner size="sm" animation="border" />
          </div>
        )}
        {configs.allow_manual_shipment_address && (
          <Footer
            onManualClick={() => {
              setModalStep('manualAddAddress');
              setShowManualForm(true);
            }}
          />
        )}
      </div>
    );
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      initialValues={{
        zipcode: '',
        city: '',
        state: '',
        street: '',
        number: '',
        country: 'Brasil',
        neighborhood: '',
        complement: '',
        reference: '',
        client_id: clientId,
      }}
      onSubmit={(values: IAddressValues) => {
        submit(values);
      }}
    >
      {(props: FormikProps<IAddressValues>) => (
        <Form className="confirm-address">{renderBody(props)}</Form>
      )}
    </Formik>
  );
}

export default SearchAddress;
